import type { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, type OnInit, signal } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EnvironmentService } from '@core/environment/environment.service';
import { Routes } from '@core/routes/routes';
import { germanDateValidator } from '@core/validators/germanDate.validator';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { IbanPipe } from '@fino-ui/finances';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { NotificationService } from '@fino-ui/notification';
import { FinoSelectModule } from '@fino-ui/select';
import { FinoTranslationModule } from '@fino-ui/translation';
import { merge } from 'lodash';
import * as moment from 'moment';
import { finalize } from 'rxjs';
import { AppService } from '../../app.service';
import { IbanCheckComponent } from '../../components/iban-check/iban-check.component';
import { NavbarService } from '../../components/navbar/navbar.service';
import { PdfViewerComponent } from '../../components/pdf-viewer/pdf-viewer.component';
import { reportForm } from '../capture-transaction/capture-transaction.form.constants';
import { COMMENT_PRESETS, WARRANT_FIELDS } from './capture-warrant.constants';
import { CaptureWarrantService } from './capture-warrant.service';

@Component({
  selector: 'app-capture-warrant',
  templateUrl: './capture-warrant.component.html',
  styleUrls: ['./capture-warrant.component.sass'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FinoTranslationModule,
    IbanCheckComponent,
    FinoSelectModule,
    FinoInputModule,
    FinoIconModule,
    IbanPipe,
    ButtonModule,
    FinoCommonModule,
    PdfViewerComponent,
  ],
})
export class CaptureWarrantComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly captureWarrantService = inject(CaptureWarrantService);
  private readonly navbarService = inject(NavbarService);
  private readonly router = inject(Router);
  private readonly envService = inject(EnvironmentService);
  private readonly appService = inject(AppService);
  private readonly notificationService = inject(NotificationService);

  public readonly noJobLeft = signal(false);
  public readonly showIbanCheck = signal(true);
  public readonly job = signal<any>(null);
  public readonly pdfUrl = signal('');
  public readonly isSubmitLoading = signal(false);

  public warrantFields = WARRANT_FIELDS;

  public readonly showAccountClosingDate = signal(false);
  public readonly hasSecondAccountOwner = signal(false);

  public reportForm: SubmittableForm = reportForm;

  public readonly reportDocumentLoading = signal(false);

  public readonly dontRenderNextJob = signal(false);

  public loadNextDocumentForm: SubmittableForm = new SubmittableForm({
    loadNext: new FormControl(true),
  });

  public reportPresetItems = COMMENT_PRESETS;

  get isLocal() {
    return this.envService.isLocal;
  }

  ngOnInit() {
    this.route.params.subscribe((r) => {
      this.renderJob(r.id);
    });

    this.loadNextDocumentForm.valueChanges.subscribe((r) => {
      const { loadNext } = r;
      this.dontRenderNextJob.set(!loadNext);
    });

    this.jobForm.controls.closeAccount.valueChanges.subscribe((r) => {
      const accountClosingDateControl =
        this.jobForm.controls.accountClosingDate;
      accountClosingDateControl.setValidators(
        r ? [Validators.required, germanDateValidator] : [],
      );
      accountClosingDateControl.updateValueAndValidity();
      this.showAccountClosingDate.set(r);
    });

    this.reportForm.controls.preset?.valueChanges.subscribe((change) => {
      this.reportForm.patchValue({
        comment: change,
      });
    });
  }

  get jobFormValid() {
    return this.jobForm.valid;
  }

  public jobForm: SubmittableForm = new SubmittableForm({
    accountOwnerOneFirstName: new FormControl('', [Validators.required]),
    accountOwnerOneLastName: new FormControl('', Validators.required),
    accountOwnerTwoFirstName: new FormControl(''),
    accountOwnerTwoLastName: new FormControl(''),
    transferCredits: new FormControl(null, [Validators.required]),
    transferDebits: new FormControl(null, [Validators.required]),
    transferStandingOrders: new FormControl(null, [Validators.required]),
    transferBalance: new FormControl(null, [Validators.required]),
    closeAccount: new FormControl(null, [Validators.required]),
    accountClosingDate: new FormControl('', []),
  });

  renderNextJob() {
    this.route.params.subscribe((r) => {
      if (r.id) {
        this.router.navigate([Routes.CaptureWarrant]);
      } else {
        this.renderJob();
      }
    });
  }

  ibanCheckSuccess($event: boolean) {
    this.showIbanCheck.set(!$event);
  }

  renderJob(id: string = '') {
    this.jobForm.reset();
    this.captureWarrantService.fetchNextJob(id).subscribe({
      next: (result: any) => {
        this.navbarService.loadCounts.next('captureWarrant');
        this.showAccountClosingDate.set(false);
        this.hasSecondAccountOwner.set(false);
        this.noJobLeft.set(false);
        this.job.set(result);
        this.appService.currentTicketId.set(result.id);
        this.showIbanCheck.set(true);

        this.pdfUrl.set(result.docUrl);
      },
      error: (result: any) => {
        if (result.status === 404) {
          this.noJobLeft.set(true);
        }
      },
    });
  }

  reportDocument() {
    this.reportDocumentLoading.set(true);
    this.captureWarrantService
      .reportDocument(this.job().id, this.reportForm.controls.comment.value)
      .pipe(finalize(() => this.reportDocumentLoading.set(false)))
      .subscribe(() => {
        this.reportForm.reset();
        this.renderNextJob();
      });
  }

  toggleSecondAccountOwner() {
    this.hasSecondAccountOwner.set(!this.hasSecondAccountOwner());
  }

  getAccountOwners() {
    const {
      accountOwnerOneFirstName,
      accountOwnerOneLastName,
      accountOwnerTwoFirstName,
      accountOwnerTwoLastName,
    } = this.jobForm.value;

    const accountOwners = [
      {
        firstName: accountOwnerOneFirstName,
        lastName: accountOwnerOneLastName,
      },
    ];

    if (this.hasSecondAccountOwner()) {
      accountOwners.push({
        firstName: accountOwnerTwoFirstName,
        lastName: accountOwnerTwoLastName,
      });
    }

    return accountOwners;
  }

  submitJob() {
    this.jobForm.markAllAsTouched();
    if (this.jobFormValid) {
      const {
        closeAccount,
        transferBalance,
        transferCredits,
        transferDebits,
        transferStandingOrders,
        accountClosingDate,
      } = this.jobForm.value;

      const accountOwners = this.getAccountOwners();

      let toBeSubmitted = {
        accountOwners,
        closeAccount,
        transferBalance,
        transferCredits,
        transferDebits,
        transferStandingOrders,
      };
      const closingDate = moment
        .utc(accountClosingDate, 'DD.MM.YYYY', 'de')
        .toISOString();

      if (accountClosingDate) {
        toBeSubmitted = merge(toBeSubmitted, {
          accountClosingDate: closingDate,
        });
      }

      this.isSubmitLoading.set(true);
      this.captureWarrantService
        .submitJob(this.job().id, toBeSubmitted)
        .pipe(finalize(() => this.isSubmitLoading.set(false)))
        .subscribe({
          next: (_r) => {
            if (this.dontRenderNextJob()) {
              this.noJobLeft.set(true);
              return;
            }
            this.renderNextJob();
          },
          error: (resp: HttpErrorResponse) => {
            this.notificationService.error(resp.message);
          },
        });
    }
  }
}
